import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle, XCircle, Clock, ArrowLeft, Home, ShoppingBag, LayoutDashboard, CreditCard } from "lucide-react";

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [countdown, setCountdown] = useState(10);

  const status = searchParams.get("status") || "success";
  const type = searchParams.get("type") || "payment"; // credit_purchase, loan_fee, stall_payment, site_plan
  const reference = searchParams.get("reference");

  // Auto-redirect countdown for successful payments
  useEffect(() => {
    if (status === "success" || status === "pending") {
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            // Redirect based on payment type
            if (type === "credit_purchase") {
              navigate("/marketplace");
            } else if (type === "loan_fee") {
              navigate("/");
            } else if (type === "stall_payment") {
              navigate("/");
            } else if (type === "site_plan") {
              navigate("/pta-admin");
            } else {
              navigate("/");
            }
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [status, type, navigate]);

  const getStatusConfig = () => {
    switch (status) {
      case "success":
        return {
          icon: <CheckCircle className="h-16 w-16 text-green-500" />,
          title: "Payment Successful!",
          description: getSuccessDescription(),
          bgColor: "bg-green-50 dark:bg-green-950/20",
          borderColor: "border-green-200 dark:border-green-800",
        };
      case "pending":
        return {
          icon: <Clock className="h-16 w-16 text-yellow-500" />,
          title: "Payment Processing",
          description: "Your payment is being processed. You will receive a confirmation email once completed.",
          bgColor: "bg-yellow-50 dark:bg-yellow-950/20",
          borderColor: "border-yellow-200 dark:border-yellow-800",
        };
      case "cancelled":
        return {
          icon: <XCircle className="h-16 w-16 text-muted-foreground" />,
          title: "Payment Cancelled",
          description: "Your payment was cancelled. No charges have been made to your account.",
          bgColor: "bg-muted/50",
          borderColor: "border-border",
        };
      case "failed":
        return {
          icon: <XCircle className="h-16 w-16 text-destructive" />,
          title: "Payment Failed",
          description: "We couldn't process your payment. Please try again or use a different payment method.",
          bgColor: "bg-destructive/10",
          borderColor: "border-destructive/30",
        };
      default:
        return {
          icon: <CheckCircle className="h-16 w-16 text-green-500" />,
          title: "Payment Complete",
          description: "Your transaction has been processed.",
          bgColor: "bg-green-50 dark:bg-green-950/20",
          borderColor: "border-green-200 dark:border-green-800",
        };
    }
  };

  const getSuccessDescription = () => {
    switch (type) {
      case "credit_purchase":
        return "Your credits have been added to your account. You can now create listings in the marketplace.";
      case "loan_fee":
        return "Your loan payment has been received. The asset owner has been notified.";
      case "stall_payment":
        return "Your stall application payment has been received. You will receive confirmation details shortly.";
      case "site_plan":
        return "Your subscription has been activated! You now have access to all features included in your plan.";
      default:
        return "Your payment has been successfully processed. Thank you for your purchase.";
    }
  };

  const getActionButtons = () => {
    switch (type) {
      case "credit_purchase":
        return (
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button onClick={() => navigate("/marketplace")} className="gap-2">
              <ShoppingBag className="h-4 w-4" />
              Go to Marketplace
            </Button>
            <Button variant="outline" onClick={() => navigate("/marketplace/create")} className="gap-2">
              Create a Listing
            </Button>
          </div>
        );
      case "loan_fee":
        return (
          <Button onClick={() => navigate("/")} className="gap-2">
            <Home className="h-4 w-4" />
            Return Home
          </Button>
        );
      case "stall_payment":
        return (
          <Button onClick={() => navigate("/")} className="gap-2">
            <Home className="h-4 w-4" />
            Return Home
          </Button>
        );
      case "site_plan":
        return (
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button onClick={() => navigate("/pta-admin")} className="gap-2">
              <LayoutDashboard className="h-4 w-4" />
              Go to Dashboard
            </Button>
            <Button variant="outline" onClick={() => navigate("/pta-admin?tab=payments")} className="gap-2">
              <CreditCard className="h-4 w-4" />
              View Your Plan
            </Button>
          </div>
        );
      default:
        return (
          <Button onClick={() => navigate("/")} className="gap-2">
            <Home className="h-4 w-4" />
            Return Home
          </Button>
        );
    }
  };

  const config = getStatusConfig();

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1 container py-12">
        <div className="max-w-lg mx-auto">
          <Card className={`${config.bgColor} ${config.borderColor} border-2`}>
            <CardHeader className="text-center pb-2">
              <div className="flex justify-center mb-4">
                {config.icon}
              </div>
              <CardTitle className="text-2xl">{config.title}</CardTitle>
            </CardHeader>
            <CardContent className="text-center space-y-6">
              <p className="text-muted-foreground">{config.description}</p>
              
              {reference && (
                <div className="bg-background/50 rounded-lg p-3">
                  <p className="text-sm text-muted-foreground">Reference</p>
                  <p className="font-mono text-sm">{reference}</p>
                </div>
              )}

              {(status === "success" || status === "pending") && (
                <p className="text-sm text-muted-foreground">
                  Redirecting in {countdown} seconds...
                </p>
              )}

              <div className="pt-4">
                {getActionButtons()}
              </div>

              {(status === "cancelled" || status === "failed") && (
                <Button 
                  variant="ghost" 
                  onClick={() => navigate(-1)} 
                  className="gap-2"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Go Back
                </Button>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default PaymentSuccess;
